import { Observable, ReplaySubject, Subscription } from 'rxjs';
import _Pusher from 'pusher-js';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import { NgZone } from '@angular/core';

import { ServerApi } from './server-api';

import { inDemoMode, inReadOnlyMode, inSupportMode } from '@app/lib/remote-login';

const CLIENT_EVENTS_PREFIX = 'client-v2-';
const CLIENT_ONLY_CHANNELS = ['hijack', 'realtime-aux-data'];
const PUSHER_KEEP_ALIVE_PING_INTERVAL: number = 5 * 60 * 1000; // 5 mins

export class ChannelWrapper {
  private _baseName;
  private name;
  private _pusher;
  private _api: ServerApi;
  private _zone: NgZone;
  private _channel;
  private _keepAliveTimeout;
  private _joinEndPointPath = 'callbacks/pusher/join';
  private _leaveEndPointPath = 'callbacks/pusher/leave';
  private _joinData;
  private lastJoinApiResultStream: ReplaySubject<boolean>;
  private joinChannelSubscription: Subscription;

  constructor(
    pusher,
    api: ServerApi,
    zone: NgZone,
    channelNamePrefix,
    id,
    baseName,
    joinData,
    endPointPathPostfix,
    enablePusherInHijack: boolean,
  ) {
    endPointPathPostfix = isNil(endPointPathPostfix) ? '' : endPointPathPostfix;
    this._joinEndPointPath += endPointPathPostfix;
    this._leaveEndPointPath += endPointPathPostfix;
    this._baseName = baseName;
    this._pusher = pusher;
    this._api = api;
    this._zone = zone;
    this._joinData = isEmpty(joinData) ? { channel_suffix: this._baseName } : joinData;
    this.name = `${channelNamePrefix}-${id}-${baseName}`;
    this._channel = pusher.subscribe(this.name);
    this._joinChannel(enablePusherInHijack); // initial join request
    this.lastJoinApiResultStream = new ReplaySubject(1);
  }

  public getEvent(name) {
    return new Observable(observer => {
      const callback = data => this._zone.run(() => observer.next(data));
      this._channel.bind(name, callback);
      return () => {
        this._channel.unbind(name, callback);
        clearTimeout(this._keepAliveTimeout);
        this._leaveChannel();
      };
    });
  }

  public getClientEvent(name) {
    return this.getEvent(`${CLIENT_EVENTS_PREFIX}${name}`);
  }

  public broadcast(name, data = {}) {
    this._channel.trigger(`${CLIENT_EVENTS_PREFIX}${name}`, data);
  }

  public unsubscribe() {
    this._pusher.unsubscribe(this.name);
  }

  public setJoinData(joinData: any = {}) {
    this._joinData = isEmpty(joinData) ? { channel_suffix: this._baseName } : joinData;
  }

  public get lastJoinApiResult$(): Observable<boolean> {
    return this.lastJoinApiResultStream ? this.lastJoinApiResultStream.asObservable() : null;
  }

  public retriggerJoinChannel(enablePusherInHijack: boolean): void {
    clearTimeout(this._keepAliveTimeout);
    this.joinChannelSubscription.unsubscribe();
    this._joinChannel(enablePusherInHijack);
  }

  private _joinChannel(enablePusherInHijack: boolean, retryAttempt = 1) {
    const baseName = this._baseName;
    if (
      ((inSupportMode() || inDemoMode() || inReadOnlyMode()) && !enablePusherInHijack) ||
      includes(CLIENT_ONLY_CHANNELS, baseName)
    ) return;
    this.joinChannelSubscription = this._api.post(this._joinEndPointPath, this._joinData).subscribe(() => {
      this.lastJoinApiResultStream.next(true);
      this._keepAliveTimeout = setTimeout(() => this._joinChannel(enablePusherInHijack), PUSHER_KEEP_ALIVE_PING_INTERVAL);
    }, () => {
      this.lastJoinApiResultStream.next(false);
      this._keepAliveTimeout = setTimeout(() => this._joinChannel(enablePusherInHijack, retryAttempt + 1), retryAttempt * 2000);
    });
  }

  private _leaveChannel() {
    const baseName = this._baseName;
    if (inSupportMode() || inDemoMode() || inReadOnlyMode() || includes(CLIENT_ONLY_CHANNELS, baseName)) return;
    this._api.post(this._leaveEndPointPath, this._joinData).subscribe(() => {
      this.lastJoinApiResultStream.next();
    });
  }

}
